import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Center, Spinner, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useVisitorPermissions } from '../hooks/useVisitorPermissions';
import { TabMenu, type TabItem } from '@themes/aegix';
import { SMSPageHeader as PageHeader } from '../components/layout/SMSPageHeader';
import {
  HiUserGroup,
  HiShieldExclamation,
  HiAcademicCap,
  HiComputerDesktop,
  HiAdjustmentsHorizontal,
} from 'react-icons/hi2';
import { ImUserTie } from 'react-icons/im';
import { VisitorsContent } from '../components/visitors/VisitorsContent';
import { EmployeeVisitContent } from '../components/visitors/EmployeeVisitContent';
import { WatchListContent } from '../components/visitors/WatchListContent';
import { KioskActivation } from '../components/kiosk';
import { StudentsList } from '../components/reunification/StudentsList';
import { VisitorManagementContent } from '../components/admin';

const tabs: TabItem[] = [
  {
    value: 'visitors',
    label: 'Visitors',
    icon: HiUserGroup,
    'data-e2e': 'tab-visitors',
  },
  {
    value: 'employee-visit',
    label: 'Employee Visit',
    icon: ImUserTie,
    'data-e2e': 'tab-employee-visit',
  },
  {
    value: 'watchlist',
    label: 'Watch List',
    icon: HiShieldExclamation,
    'data-e2e': 'tab-watchlist',
  },
  {
    value: 'students',
    label: 'Students',
    icon: HiAcademicCap,
    'data-e2e': 'tab-students',
  },
  {
    value: 'kiosk',
    label: 'Kiosk',
    icon: HiComputerDesktop,
    'data-e2e': 'tab-kiosk',
  },
  {
    value: 'configure',
    label: 'Configure',
    icon: HiAdjustmentsHorizontal,
    'data-e2e': 'tab-configure',
  },
];

const subtitles: Record<string, string> = {
  visitors: 'Visitor Check-In & Check-Out',
  'employee-visit': 'Employee Visit',
  watchlist: 'Flagged Individuals',
  students: 'Student Roster',
  kiosk: 'Configure Kiosk Mode',
  configure: 'Visit reasons, categories, badge templates, screening, roster',
};

export function VisitorMgmt() {
  const { t } = useTranslation();
  usePageTitle(t('visitorMgmt.title', 'Visitor Management'));
  const [searchParams, setSearchParams] = useSearchParams();
  const { canObserve, canManage, isLoading } = useVisitorPermissions();

  const localizedTabs = useMemo(() => {
    return tabs.map((tab) => ({
      ...tab,
      label: t(`visitorMgmt.tab.${tab.value}`, tab.label),
    }));
  }, [t]);

  // Watchlist and configure tabs are Manage-only (FERPA-sensitive admin/watchlist
  // surfaces); students & kiosk tabs stay readable at observe-tier.
  const visibleTabs = useMemo(() => {
    return localizedTabs.filter((t) => {
      if ((t.value === 'watchlist' || t.value === 'configure') && !canManage) {
        return false;
      }
      return true;
    });
  }, [localizedTabs, canManage]);

  const rawTab = searchParams.get('tab') || 'visitors';
  const activeTab = useMemo(() => {
    if ((rawTab === 'watchlist' || rawTab === 'configure') && !canManage) {
      return 'visitors';
    }
    return rawTab;
  }, [rawTab, canManage]);

  useEffect(() => {
    if (
      (rawTab === 'watchlist' || rawTab === 'configure') &&
      !canManage &&
      !isLoading
    ) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'visitors');
          return next;
        },
        { replace: true },
      );
    }
  }, [rawTab, canManage, isLoading, setSearchParams]);

  const handleTabChange = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      next.delete('subtab');
      next.delete('id');
      next.delete('public_id');
      return next;
    });
  };

  const currentSubtitle = t(
    `visitorMgmt.subtitle.${activeTab}`,
    subtitles[activeTab] || '',
  );

  if (isLoading) {
    return (
      <Box mx={-8} mt={-8}>
        <PageHeader
          icon={HiUserGroup as React.ElementType}
          title={t('visitorMgmt.title', 'Visitor Management')}
          subtitle=""
          showSearch={true}
          showNotifications={true}
        />
        <Center py={12}>
          <Spinner size="lg" color="button.primary" />
        </Center>
      </Box>
    );
  }

  if (!canObserve) {
    return (
      <Box mx={-8} mt={-8}>
        <PageHeader
          icon={HiUserGroup as React.ElementType}
          title={t('visitorMgmt.title', 'Visitor Management')}
          subtitle=""
          showSearch={true}
          showNotifications={true}
        />
        <Center py={12}>
          <Text color="text.secondary" data-e2e="visitor-mgmt-access-denied">
            {t(
              'visitorMgmt.accessDenied',
              'You do not have permission to view Visitor Management.',
            )}
          </Text>
        </Center>
      </Box>
    );
  }

  return (
    <Box mx={-8} mt={-8}>
      <PageHeader
        icon={HiUserGroup as React.ElementType}
        title={t('visitorMgmt.title', 'Visitor Management')}
        subtitle={currentSubtitle}
        showSearch={true}
        showNotifications={true}
      />
      <TabMenu
        tabs={visibleTabs}
        activeTab={activeTab}
        onChange={handleTabChange}
        e2eTitle="VisitorMgmtTabs"
      />
      {activeTab === 'visitors' && <VisitorsContent />}
      {activeTab === 'employee-visit' && <EmployeeVisitContent />}
      {activeTab === 'watchlist' && canManage && <WatchListContent />}
      {activeTab === 'students' && <StudentsList />}
      {activeTab === 'kiosk' && <KioskActivation />}
      {activeTab === 'configure' && canManage && (
        <VisitorManagementContent
          initialSubTab={searchParams.get('subtab') || undefined}
        />
      )}
    </Box>
  );
}
